import React, { useState, useEffect } from 'react'
import {
	Box,
	Button,
	Card,
	Chip,
	Grid,
	IconButton,
	Stack,
	TextField,
	Typography,
	CircularProgress,
	Alert,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Globe, Plus, Star, Trash2 } from 'react-feather'
import { format, addHours } from 'date-fns'

import { createSchedule } from '../../api/scheduler/createSchedule'
import { fetchSchedules, Schedule } from '../../api/scheduler/fetchSchedules'
import { deleteSchedule } from '../../api/scheduler/deleteSchedule'
import { useNotification } from '../../components/Notification/NotificationContext'
import useSearchCampaign from '../../api/campaign/useSearchCampaign'
import { Campaign } from '../../api/campaign/types'
import { useFeatureFlags } from '../../utils/FeatureFlags/useFeatureFlags'
import NotFoundPage from '../NotFound/NotFoundPage'

interface ScheduleInput {
	id: string
	date: Date | null
	time: string
}

export default function SchedulerManagerPage() {
	const { palette } = useTheme()
	const { addMessage } = useNotification()
	const { FRONTEND_SCHEDULER_MANAGER } = useFeatureFlags()
	const [schedules, setSchedules] = useState<Schedule[]>([])
	const [loading, setLoading] = useState(false)
	const [schedulesLoading, setSchedulesLoading] = useState(false)
	const [scheduleInputs, setScheduleInputs] = useState<ScheduleInput[]>([{ id: '1', date: null, time: '13:00' }])
	const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
	const [timezone, setTimezone] = useState(() => {
		// Get timezone from browser
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone
		} catch (error) {
			// Fallback to a default timezone if browser doesn't support it
			return 'America/Phoenix'
		}
	})

	// Fetch campaigns for dropdown
	const { campaigns } = useSearchCampaign('scheduler-campaigns', {
		page_size: 100,
		page_number: 1,
		sort_order: 'desc',
		sort_by: 'created_at',
	})

	// Fetch existing schedules on component mount
	useEffect(() => {
		fetchExistingSchedules()
	}, [])

	const fetchExistingSchedules = async () => {
		try {
			setSchedulesLoading(true)
			const response = await fetchSchedules()
			setSchedules(response.schedules)
		} catch (error) {
			console.error('Error fetching schedules:', error)
			addMessage({ error: 'Failed to fetch schedules' })
		} finally {
			setSchedulesLoading(false)
		}
	}

	const handleQuickAdd = (type: 'today' | 'tomorrow' | 'monday') => {
		const now = new Date()
		let targetDate: Date

		switch (type) {
			case 'today':
				targetDate = addHours(now, 2)
				break
			case 'tomorrow':
				targetDate = new Date()
				targetDate.setDate(now.getDate() + 1)
				targetDate.setHours(9, 0, 0, 0)
				break
			case 'monday':
				const daysUntilMonday = (8 - now.getDay()) % 7 || 7
				targetDate = new Date()
				targetDate.setDate(now.getDate() + daysUntilMonday)
				targetDate.setHours(9, 0, 0, 0)
				break
		}

		const newInput: ScheduleInput = {
			id: Date.now().toString(),
			date: targetDate,
			time: format(targetDate, 'HH:mm'),
		}

		setScheduleInputs((prev) => [...prev, newInput])
	}

	const handleAddAnotherTime = () => {
		const newInput: ScheduleInput = {
			id: Date.now().toString(),
			date: null,
			time: '13:00',
		}
		setScheduleInputs((prev) => [...prev, newInput])
	}

	const handleRemoveSchedule = (id: string) => {
		setScheduleInputs((prev) => prev.filter((input) => input.id !== id))
	}

	const handleDateChange = (id: string, date: Date | null) => {
		setScheduleInputs((prev) => prev.map((input) => (input.id === id ? { ...input, date } : input)))
	}

	const handleTimeChange = (id: string, time: string) => {
		setScheduleInputs((prev) => prev.map((input) => (input.id === id ? { ...input, time } : input)))
	}

	const handleCreateSchedules = async () => {
		if (!selectedCampaign) {
			addMessage({ error: 'Please select a campaign' })
			return
		}

		const validSchedules = scheduleInputs.filter((input) => input.date)
		if (validSchedules.length === 0) {
			addMessage({ error: 'Please add at least one schedule time' })
			return
		}

		setLoading(true)
		try {
			const scheduledTimes = validSchedules
				.filter((schedule) => schedule.date !== null)
				.map((schedule) => {
					const [hours, minutes] = schedule.time.split(':').map(Number)
					// Create a new date object to avoid mutating the original
					const scheduleDate = new Date(schedule.date!)
					// Set the time components explicitly
					scheduleDate.setHours(hours, minutes, 0, 0)

					// Validate the date is not in the past
					const now = new Date()
					if (scheduleDate <= now) {
						throw new Error(`Schedule time cannot be in the past`)
					}

					// Return date in YYYY-MM-DDTHH:mm:ss format without timezone
					const year = scheduleDate.getFullYear()
					const month = String(scheduleDate.getMonth() + 1).padStart(2, '0')
					const day = String(scheduleDate.getDate()).padStart(2, '0')
					const hour = String(scheduleDate.getHours()).padStart(2, '0')
					const minute = String(scheduleDate.getMinutes()).padStart(2, '0')
					const second = String(scheduleDate.getSeconds()).padStart(2, '0')

					return `${year}-${month}-${day}T${hour}:${minute}:${second}`
				})

			// Debug: Log the scheduled times being sent
			console.log('Sending scheduled times:', scheduledTimes, 'with timezone:', timezone)

			await createSchedule(selectedCampaign.campaign_id, scheduledTimes, timezone, selectedCampaign.campaign_name)

			addMessage({ success: `Successfully scheduled ${validSchedules.length} time(s)` })
			setScheduleInputs([{ id: '1', date: null, time: '13:00' }])
			setSelectedCampaign(null)
			fetchExistingSchedules()
		} catch (error) {
			console.error('Error creating schedules:', error)

			// Check if it's a validation error vs API error
			if (error instanceof Error && error.message.includes('cannot be in the past')) {
				addMessage({ error: error.message })
			} else if (error instanceof Error && error.message.includes('Invalid Schedule Expression')) {
				addMessage({ error: 'Invalid schedule time format. Please check your date and time selections.' })
			} else {
				addMessage({ error: 'Failed to create schedules. Please try again.' })
			}
		} finally {
			setLoading(false)
		}
	}

	const handleDeleteSchedule = async (scheduleName: string) => {
		try {
			await deleteSchedule(scheduleName)
			addMessage({ success: 'Schedule deleted successfully' })
			fetchExistingSchedules()
		} catch (error) {
			console.error('Error deleting schedule:', error)
			addMessage({ error: 'Failed to delete schedule' })
		}
	}

	const getStatusColor = (state: string) => {
		switch (state) {
			case 'ENABLED':
				return 'success'
			case 'DISABLED':
				return 'error'
			default:
				return 'default'
		}
	}

	const formatScheduleTime = (scheduleExpression: string, tz: string) => {
		try {
			// Extract time from schedule expression like "at(2025-09-02T13:00:00)"
			const timeMatch = scheduleExpression.match(/at\(([^)]+)\)/)
			if (timeMatch) {
				const date = new Date(timeMatch[1])
				return format(date, 'EEE, MMM d · h:mm a')
			}
			return 'Invalid schedule expression'
		} catch (error) {
			return 'Invalid date'
		}
	}

	if (!FRONTEND_SCHEDULER_MANAGER) {
		return <NotFoundPage />
	}

	return (
		<Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
			{/* Header with gradient */}
			<Box
				sx={{
					background: 'linear-gradient(90deg, #6528F7 0%, #FF6B9D 100%)',
					height: '4px',
					borderRadius: '2px',
					mb: 2,
				}}
			/>

			<Stack direction="row" alignItems="center" spacing={2} mb={3}>
				<Box
					sx={{
						width: 40,
						height: 40,
						background: palette.brand.main,
						borderRadius: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Star size={20} color="white" />
				</Box>
				<Box>
					<Typography variant="h4" sx={{ fontWeight: 600, color: palette.text.primary }}>
						Schedule calls for later
					</Typography>
					<Typography variant="body1" sx={{ color: palette.text.lightGrey, mt: 0.5 }}>
						Create one or more time slots to run your Prop.ai AI outbound calling campaigns.
					</Typography>
				</Box>
			</Stack>

			<Grid container spacing={4}>
				{/* Left Section - Scheduling Inputs */}
				<Grid item xs={12} md={7}>
					<Stack spacing={3}>
						{/* Quick Add Buttons */}
						<Stack direction="row" spacing={2} flexWrap="wrap">
							<Button
								variant="outlined"
								onClick={() => handleQuickAdd('today')}
								sx={{
									borderColor: palette.text.lightGrey,
									color: palette.text.primary,
									'&:hover': {
										borderColor: palette.brand.main,
										color: palette.brand.main,
									},
								}}
							>
								Add later today
							</Button>
							<Button
								variant="outlined"
								onClick={() => handleQuickAdd('tomorrow')}
								sx={{
									borderColor: palette.text.lightGrey,
									color: palette.text.primary,
									'&:hover': {
										borderColor: palette.brand.main,
										color: palette.brand.main,
									},
								}}
							>
								Add tomorrow 9:00 AM
							</Button>
							<Button
								variant="outlined"
								onClick={() => handleQuickAdd('monday')}
								sx={{
									borderColor: palette.text.lightGrey,
									color: palette.text.primary,
									'&:hover': {
										borderColor: palette.brand.main,
										color: palette.brand.main,
									},
								}}
							>
								Add next Monday 9:00 AM
							</Button>
						</Stack>

						{/* Campaign Selection */}
						<TextField
							select
							placeholder="Choose a campaign to schedule"
							value={selectedCampaign?.campaign_id || ''}
							onChange={(e) => {
								const selectedCampaignData = campaigns.find(
									(c: Campaign) => c.campaign_id === e.target.value,
								)
								setSelectedCampaign(selectedCampaignData || null)
							}}
							fullWidth
							SelectProps={{
								native: true,
							}}
						>
							<option value="" disabled>
								Select a campaign
							</option>
							{campaigns.map((campaign: Campaign) => (
								<option key={campaign.campaign_id} value={campaign.campaign_id}>
									{campaign.campaign_name}
								</option>
							))}
						</TextField>

						{/* Schedule Inputs */}
						{scheduleInputs.map((input, index) => (
							<Card key={input.id} sx={{ p: 3, position: 'relative' }}>
								<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
									<Chip
										label={`Schedule ${index + 1}`}
										size="small"
										sx={{
											backgroundColor: palette.sidebar.gray,
											color: palette.text.darkGrey,
										}}
									/>
									<IconButton
										size="small"
										onClick={() => handleRemoveSchedule(input.id)}
										sx={{ color: palette.text.lightGrey }}
									>
										<Trash2 size={16} />
									</IconButton>
								</Stack>

								<Grid container spacing={2}>
									<Grid item xs={6}>
										<TextField
											label="Date"
											type="date"
											value={input.date ? format(input.date, 'yyyy-MM-dd') : ''}
											onChange={(e) => {
												const value = e.target.value
												if (value && value.length === 10) {
													const [year, month, day] = value.split('-').map(Number)
													if (year && month && day) {
														const date = new Date(year, month - 1, day)
														handleDateChange(input.id, date)
													}
												} else if (!value) {
													handleDateChange(input.id, null)
												}
											}}
											fullWidth
											inputProps={{
												min: format(new Date(), 'yyyy-MM-dd'),
											}}
											InputLabelProps={{
												shrink: true,
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											label="Time"
											type="time"
											value={input.time}
											onChange={(e) => handleTimeChange(input.id, e.target.value)}
											fullWidth
											inputProps={{
												min:
													input.date &&
													format(input.date, 'yyyy-MM-dd') ===
														format(new Date(), 'yyyy-MM-dd')
														? format(new Date(), 'HH:mm')
														: '00:00',
											}}
											InputLabelProps={{
												shrink: true,
											}}
										/>
									</Grid>
								</Grid>
							</Card>
						))}

						{/* Add Another Time Button */}
						<Button
							variant="contained"
							onClick={handleAddAnotherTime}
							startIcon={<Plus size={20} />}
							sx={{
								background: 'linear-gradient(135deg, #6528F7 0%, #8B5CF6 100%)',
								color: 'white',
								py: 1.5,
								'&:hover': {
									background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 100%)',
								},
							}}
						>
							Add another time
						</Button>

						{/* Timezone Information */}
						<Stack direction="row" alignItems="center" spacing={1}>
							<Globe size={16} color={palette.text.lightGrey} />
							<Typography variant="body2" sx={{ color: palette.text.lightGrey }}>
								Scheduling in: {timezone}
							</Typography>
						</Stack>
					</Stack>
				</Grid>

				{/* Right Section - Schedule Summary */}
				<Grid item xs={12} md={5}>
					<Card sx={{ p: 3, height: 'fit-content' }}>
						<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
							Schedule summary
						</Typography>

						<Box
							sx={{
								backgroundColor: '#F3F4F6',
								borderRadius: 1,
								p: 2,
								mb: 2,
							}}
						>
							<Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
								Planned starts ({scheduleInputs.filter((s) => s.date).length})
							</Typography>
							{scheduleInputs
								.filter((schedule) => schedule.date)
								.map((schedule, index) => (
									<Box key={schedule.id}>
										<Typography variant="body2" sx={{ color: palette.text.darkGrey }}>
											{format(schedule.date!, 'EEE, MMM d · h:mm a')}
										</Typography>
										<Typography variant="caption" sx={{ color: palette.text.lightGrey }}>
											({timezone})
										</Typography>
									</Box>
								))}
						</Box>

						{/* Action Buttons */}
						<Stack direction="row" spacing={2}>
							<Button
								variant="outlined"
								fullWidth
								sx={{
									borderColor: palette.text.lightGrey,
									color: palette.text.primary,
								}}
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								onClick={handleCreateSchedules}
								disabled={
									loading || scheduleInputs.filter((s) => s.date).length === 0 || !selectedCampaign
								}
								fullWidth
								sx={{
									background: 'linear-gradient(135deg, #6528F7 0%, #8B5CF6 100%)',
									color: 'white',
									'&:hover': {
										background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 100%)',
									},
									'&:disabled': {
										background: palette.text.lightGrey,
									},
								}}
							>
								{loading ? (
									<CircularProgress size={20} color="inherit" />
								) : (
									`Schedule ${scheduleInputs.filter((s) => s.date).length} time${scheduleInputs.filter((s) => s.date).length !== 1 ? 's' : ''}`
								)}
							</Button>
						</Stack>
					</Card>
				</Grid>
			</Grid>

			{/* Existing Schedules Section */}
			<Box sx={{ mt: 6 }}>
				<Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
					Existing Schedules
				</Typography>

				{schedulesLoading ? (
					<Box display="flex" justifyContent="center" p={4}>
						<CircularProgress />
					</Box>
				) : schedules.length === 0 ? (
					<Alert severity="info">No scheduled campaigns found.</Alert>
				) : (
					<Stack spacing={2}>
						{schedules.map((schedule) => (
							<Card key={schedule.schedule_name} sx={{ p: 3 }}>
								<Stack direction="row" justifyContent="space-between" alignItems="center">
									<Box>
										<Typography variant="h6" sx={{ fontWeight: 500 }}>
											{schedule.description || 'Unnamed Schedule'}
										</Typography>
										<Typography variant="body2" sx={{ color: palette.text.lightGrey }}>
											{formatScheduleTime(schedule.schedule_expression, schedule.timezone)}
										</Typography>
										<Typography
											variant="caption"
											sx={{ color: palette.text.lightGrey, display: 'block', mt: 0.5 }}
										>
											{schedule.timezone}
										</Typography>
									</Box>
									<Stack direction="row" spacing={2} alignItems="center">
										<Chip
											label={schedule.state}
											color={getStatusColor(schedule.state) as any}
											size="small"
										/>
										<IconButton
											size="small"
											onClick={() => handleDeleteSchedule(schedule.schedule_name)}
											sx={{ color: palette.text.lightGrey }}
										>
											<Trash2 size={16} />
										</IconButton>
									</Stack>
								</Stack>
							</Card>
						))}
					</Stack>
				)}
			</Box>
		</Box>
	)
}
